import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
  { id: "people", label: "People", to: "/people" },
  { id: "opportunities", label: "Talent Network" },
  { id: "network", label: "Join" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (link) => {
    setOpen(false);
    // Route-based nav (e.g. /people)
    if (link.to) {
      navigate(link.to);
      return;
    }
    const id = link.id;
    // If we're already on the homepage, smooth-scroll to the section.
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    // Otherwise navigate home with a hash — Home reads location.hash and scrolls.
    navigate(`/#${id}`);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b transition-colors duration-500 ${
        scrolled
          ? "border-white/10 bg-[#0E2750]/85"
          : "border-transparent bg-[#0E2750]/30"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between h-20 md:h-24">
        <button
          data-testid="logo-link"
          onClick={() => goTo({ id: "hero" })}
          className="flex flex-col items-start leading-none group"
          aria-label="ArcPoint Executive Search — home"
        >
          <span className="font-brand text-base md:text-lg tracking-[0.08em] text-white">
            ArcPoint
          </span>
          <span className="text-[9px] md:text-[10px] uppercase tracking-[0.32em] text-slate-300/80 mt-1.5">
            Executive Search
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => goTo(l)}
              className="text-[13px] tracking-[0.12em] uppercase text-slate-200 hover:text-white gold-underline transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-book-call"
            onClick={() => goTo({ id: "contact" })}
            className="hidden md:inline-flex items-center px-5 py-2.5 border border-[#C9A876]/60 text-[#C9A876] hover:bg-[#C9A876] hover:text-[#0E2750] text-[12px] uppercase tracking-[0.18em] font-medium transition-all duration-500"
          >
            Book a Call
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
            className="lg:hidden text-white p-2"
          >
            {open ? <X size={20} strokeWidth={1.25} /> : <Menu size={20} strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden border-t border-white/10 bg-[#0E2750]/98 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => goTo(l)}
                className="text-left text-base text-slate-200 hover:text-[#C9A876] py-2"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-cta-book-call"
              onClick={() => goTo({ id: "contact" })}
              className="mt-2 inline-flex items-center justify-center px-5 py-3 border border-[#C9A876] text-[#C9A876] uppercase tracking-[0.18em] text-sm"
            >
              Book a Confidential Call
            </button>
            <Link
              to="/admin/login"
              data-testid="mobile-admin-link"
              className="text-xs uppercase tracking-[0.25em] text-slate-500 mt-2"
            >
              Admin
            </Link>
          </div>
        </motion.div>
      )}
    </header>
  );
}
